
    // Carries the value over when the discount type is switched: the
    // percent the old type stands for is written into the new type's
    // field — the percent itself, the sum it takes off the order (before
    // discount, VAT included, as sums are typed), or "N%" for "Apply to
    // all positions". Per-item discounts stand for the percent in that
    // field (a plain sum there is the order's discount with VAT), else
    // the one every row it writes to shares ("data-row-percent").
    // Through "None" the last percent is kept. Nothing is applied here.
    //
    // The catalogues (owner, 2026-10-05, spec §6.2): a sum is converted
    // over the rows of the catalogues "Apply" leaves the discount on —
    // the setting's ("data-gross-setting") unless the order keeps its
    // own set ("data-keeps-snapshot": an order-wide discount with a set,
    // the box not ticked), then the order's own ("data-gross"); "Apply
    // to all positions" always writes by the setting. The line naming
    // those catalogues, the box and the "nothing falls under it" hint
    // follow the type and the box.
    export default {
      mounted() {
        this.reset()
        this.el.addEventListener("change", (e) => {
          if (e.target.name === "type") {
            this.carry(this.type, e.target.value)
            this.type = e.target.value
          }
          if (e.target.name === "type" || e.target.name === "apply_setting") this.scope()
        })
        this.el.addEventListener("input", (e) => {
          if (e.target.name === "bulk_value") this.hint()
        })
      },
      updated() {
        this.reset()
      },
      reset() {
        const radio = this.el.querySelector("input[name=type]:checked")
        this.type = radio ? radio.value : "none"
        this.last = null
        this.hint()
        this.scope()
      },
      field(name) {
        return this.el.querySelector(`input[name=${name}]`)
      },
      // As `PhoenixKit.Utils.Number.parse_decimal/2` reads it: spaces
      // group; with both "." and "," the last one is the decimal mark;
      // a mark repeated is grouping.
      number(text) {
        if (text == null) return null
        let t = String(text).replace(/[\s\u00A0\u2009\u202F]+/g, "")
        if (t === "") return null
        const dots = t.split(".").length - 1
        const commas = t.split(",").length - 1
        if (dots && commas) {
          const mark = t.lastIndexOf(".") > t.lastIndexOf(",") ? "." : ","
          t = t.split(mark === "." ? "," : ".").join("").replace(",", ".")
        } else if (commas > 1) t = t.split(",").join("")
        else if (dots > 1) t = t.split(".").join("")
        else t = t.replace(",", ".")
        return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(t) ? parseFloat(t) : null
      },
      round(n, places) {
        const f = 10 ** places
        return String(Math.round(n * f) / f)
      },
      // Whether "Apply" writes the setting's catalogues: an order
      // without an order-wide discount with a set takes them, one with
      // it keeps its own unless the box is ticked.
      bySetting() {
        const box = this.field("apply_setting")
        return this.el.dataset.keepsSnapshot !== "true" || (box != null && box.checked)
      },
      gross() {
        const data = this.el.dataset
        return this.number(this.bySetting() ? data.grossSetting : data.gross)
      },
      share(sum, gross) {
        return gross > 0 && sum != null ? Math.min(sum / gross * 100, 100) : null
      },
      bulkShare(sum) {
        return this.share(sum, this.number(this.el.dataset.grossSetting))
      },
      percent(type) {
        const value = this.number(this.field("value").value)
        if (type === "percent") return value
        if (type === "amount") return this.share(value, this.gross())
        if (type === "custom") {
          const bulk = this.field("bulk_value").value.trim()
          if (bulk.endsWith("%")) return this.number(bulk.slice(0, -1))
          if (bulk !== "") return this.bulkShare(this.number(bulk))
          return this.number(this.el.dataset.rowPercent)
        }
        return this.last
      },
      carry(from, to) {
        const percent = this.percent(from)
        if (percent == null || !(percent > 0)) return
        this.last = percent
        const gross = this.gross()
        if (to === "percent") this.field("value").value = this.round(percent, 4)
        if (to === "amount" && gross > 0)
          this.field("value").value = this.round(gross * percent / 100, 2)
        if (to === "custom") {
          this.field("bulk_value").value = this.round(percent, 4) + "%"
          this.hint()
        }
      },
      hint() {
        const hint = this.el.querySelector("#discount-bulk-percent")
        if (!hint) return
        const bulk = this.field("bulk_value").value.trim()
        const percent = bulk === "" || bulk.endsWith("%") ? null : this.bulkShare(this.number(bulk))
        hint.style.display = percent == null ? "none" : ""
        if (percent != null)
          hint.textContent = hint.dataset.template.replace("%{percent}", this.round(percent, 4))
      },
      show(id, visible) {
        const el = this.el.querySelector(`#${id}`)
        if (el) el.style.display = visible ? "" : "none"
      },
      scope() {
        const wide = this.type === "percent" || this.type === "amount"
        const bySetting = this.bySetting()
        const data = this.el.dataset
        const nothing = bySetting ? data.settingCoversNothing : data.coversNothing
        this.show("discount-apply-setting", wide)
        this.show("discount-applies-snapshot", wide && !bySetting)
        this.show("discount-applies-setting", wide && bySetting)
        this.show("discount-applies-bulk", this.type === "custom")
        this.show("discount-nothing-covered", wide && nothing === "true")
      }
    }
  